// ─── ThemeProvider.jsx ────────────────────────────────────────────────────
// Wraps the app so components can read the active theme via useTheme().
//
// The themeMode value lives on the user's profile (`profiles/{uid}.themeMode`,
// 'red' | 'white'). Updates via setThemeMode() write to Firestore so the
// preference syncs across devices. Default is 'red' until the profile loads.
//
// Why a profile field rather than localStorage:
//   - Sticks across devices (iPhone / Web / iPad)
//   - Survives sign-out -> sign-in
//   - Synced via the same App.jsx onSnapshot listener that handles the
//     other profile fields, so theme changes are realtime if an admin
//     forces one
//
// Why a context (vs a Zustand store, etc.):
//   - The app already passes user / userTier / etc. through props; the
//     theme follows the same shape
//   - One subscriber (each component) re-renders only when the theme
//     reference changes (it's a frozen object so === works)

import { createContext, useContext, useEffect, useMemo } from 'react'
import { resolveTheme, DEFAULT_THEME } from '../theme.js'
// Static import — Capacitor plugins must be bundled (not externalized).
// See src/utils/auth.js for the full rationale. ~6 KB gzipped combined
// with @capacitor/core. The web stub returns no-ops/errors but the
// isNativePlatform() guard below means it's never invoked on web.
import { StatusBar, Style } from '@capacitor/status-bar'

// Default context value: red theme, no-op setter. Real values are
// supplied by <ThemeProvider> below; the default just keeps the typings
// clean for components that mount before the provider wraps them.
const ThemeContext = createContext({
  theme:       resolveTheme(DEFAULT_THEME),
  themeMode:   DEFAULT_THEME,
  setThemeMode: () => {},
})

// Hook used by every consumer: `const { theme, themeMode, setThemeMode } = useTheme()`.
// The frozen theme object (from theme.js) means components can directly
// destructure `const { colors, text, borders } = useTheme().theme` without
// worrying about mutation.
export function useTheme() {
  return useContext(ThemeContext)
}

// Provider props:
//   themeMode    — current 'red' | 'white' string (from App.jsx state,
//                  fed by the profile listener)
//   setThemeMode — callback that writes the new mode to Firestore. App.jsx
//                  owns the actual write since it already has the user.uid
//                  and the profile ref handy.
//   children     — the app subtree
export function ThemeProvider({ themeMode = DEFAULT_THEME, setThemeMode = () => {}, children }) {
  // useMemo so `theme` is stable across renders unless themeMode changes.
  // Keeps useTheme() consumers from re-rendering on every parent update.
  const value = useMemo(() => ({
    theme:       resolveTheme(themeMode),
    themeMode,
    setThemeMode,
  }), [themeMode, setThemeMode])

  // Set the data-theme attribute on <html> so index.css can cascade
  // theme-specific overrides. The CSS variables defined under
  // [data-theme="white"] in index.css then cascade through the whole
  // app — every page that uses `var(--app-*)` or the `.app-*` utility
  // classes flips automatically without per-component changes.
  //
  // We also keep the inline body/html background override as a belt-
  // and-suspenders guard for the narrow strip between page content and
  // the fixed BottomNav.
  useEffect(() => {
    const bg = value.theme.colors.bg
    document.documentElement.dataset.theme = value.themeMode
    document.documentElement.style.background = bg
    document.body.style.background = bg
  }, [value.theme.colors.bg, value.themeMode])

  // iOS native status-bar sync (Capacitor only — no-op on web).
  // The iPhone status bar (time / signal / battery) renders ABOVE
  // our WebView chrome. iOS picks the icon color based on the
  // status bar STYLE we declare via @capacitor/status-bar.
  //
  // GOTCHA (corrected 2026-05-24 after seeing the inversion live):
  // Capacitor's Style enum describes the MODE, not the content color.
  // From the @capacitor/status-bar docs:
  //   Style.Dark  = "dark-mode style"  → LIGHT text (for dark bgs)
  //   Style.Light = "light-mode style" → DARK text  (for light bgs)
  // The "Dark"/"Light" labels match iOS-native conventions for
  // appearance MODE, not for the text color produced. So:
  //   themeMode='red'   (dark cellar bg)  → Style.Dark   (light text)
  //   themeMode='white' (light ivory bg)  → Style.Light  (dark text)
  //
  // Without this sync, the static config in capacitor.config.json
  // sets one style forever — so swapping the in-app theme leaves
  // the icons invisible against the new bg. Wiring it here means
  // any theme change (manual toggle, profile sync from another
  // device) updates both the WebView AND the iOS chrome.
  //
  // isNativePlatform() guard so plugin calls are skipped on web —
  // the web stubs throw "not implemented" if called.
  useEffect(() => {
    const cap = typeof window !== 'undefined' ? window.Capacitor : undefined
    if (typeof cap?.isNativePlatform !== 'function' || !cap.isNativePlatform()) return
    const style = value.themeMode === 'white' ? Style.Light : Style.Dark
    StatusBar.setStyle({ style }).catch(err => {
      console.debug('[Theme] StatusBar.setStyle failed (non-blocking):', err?.message)
    })
    // iOS only — Android version of setBackgroundColor is no-op
    // on iOS but doesn't throw; passing the theme bg keeps the
    // status-bar tint flush with the page on both platforms.
    StatusBar.setBackgroundColor?.({ color: value.theme.colors.bg }).catch(() => { /* ignore */ })

    // Keyboard input-accessory bar (the iOS "Done" toolbar above the keys)
    // follows KeyboardStyle. Without this it's a black slab on the light
    // ivory theme. Uses the global plugin proxy — same rationale as
    // initNativeKeyboard.js (a dynamic import('@capacitor/keyboard') created
    // a boot-time chunk whose preload could white-screen the WKWebView).
    //   white theme (light bg) → 'LIGHT'   (light keyboard + bar)
    //   red theme  (dark bg)   → 'DARK'
    const kbStyle = value.themeMode === 'white' ? 'LIGHT' : 'DARK'
    cap.Plugins?.Keyboard?.setStyle?.({ style: kbStyle })?.catch?.(() => { /* ignore */ })
  }, [value.themeMode, value.theme.colors.bg])

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}
